import FeatureGates, { type GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import type { BackendExperiment } from '@atlassian/search-experiment/types';

import {
	FEDERATED_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
} from '../../constants/schemas/3p-config';

/**
 * Transforms the requested backend experiments by replacing the 'definitions' field
 */
export function getRequestedBackendExperiment(
	requestedBackendExperiments: BackendExperiment | undefined,
): BackendExperiment | undefined {
	const experimentLayers = requestedBackendExperiments?.experimentLayers?.map((layer) => {
		const { analyticsDefinitions, ...rest } = layer;
		return {
			...rest,
			definitions: analyticsDefinitions,
		};
	});

	return {
		experimentLayers,
	};
}

/**
 * Determines whether Slack is federated based on the connector sync type
 * from the third-party configuration API response.
 *
 * @param thirdPartyConfigs - Optional third party configs containing connector source info
 * @returns boolean - Whether the Slack connector is using federated sync
 */
export const isSlackFederatedByConnectorSyncType = (
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): boolean => {
	return thirdPartyConfigs?.slack?.connectorSources?.includes(FEDERATED_CONNECTOR_SOURCE) ?? false;
};

/**
 * Determines whether federated Slack search is enabled, using the connector status
 * transition feature flag when available, and falling back to the legacy experiment
 * and feature flag logic otherwise.
 *
 * @param selectedProduct - The currently selected product (string, or undefined for all products)
 * @param selectedProducts - Array of selected products (takes precedence over selectedProduct if provided)
 * @param options - Options for getting the experiment value
 * @param thirdPartyConfigs - Optional third party configs for auth check and connector source info
 * @returns boolean - Whether federated Slack search is enabled
 */
export const serveFederatedSlackExperiment = (
	selectedProduct?: string,
	selectedProducts?: string[],
	options: GetExperimentValueOptions<string> = {
		fireExperimentExposure: false,
	},
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): boolean => {
	// Determine effective selected product:
	// - If selectedProducts array is provided and non-empty, use it to determine the product
	// - Otherwise fall back to selectedProduct
	const effectiveSelectedProduct =
		selectedProducts && selectedProducts.length > 0
			? selectedProducts.includes('slack')
				? 'slack'
				: selectedProducts[0]
			: selectedProduct;

	// Only serve the experiment when user is under all product search or Slack product search
	const isSlackOrAllProducts = !effectiveSelectedProduct || effectiveSelectedProduct === 'slack';
	if (!isSlackOrAllProducts) {
		return false;
	}

	// Return false if Slack is not authorized (skip check if thirdPartyConfigs not provided)
	if (thirdPartyConfigs) {
		const isSlackAuthorized = thirdPartyConfigs.slack?.userNeedsOAuth === false;
		if (!isSlackAuthorized) {
			return false;
		}
	}

	// When the connector status transition FF is enabled, use the actual connector sync type
	// to determine if Slack should use federated search behavior
	if (fg('ai_3p_slack_connector_status_transition_enabled')) {
		return isSlackFederatedByConnectorSyncType(thirdPartyConfigs);
	}

	// Fallback to legacy experiment and feature flag logic
	const experimentValue = FeatureGates.getExperimentValue<string>(
		'ai-3p-slack-federated-search-analysis',
		'expValue',
		'legacy',
		options,
	);
	return (
		experimentValue === 'fed' ||
		experimentValue === 'fed-optimized' ||
		fg('ai_3p_slack_federated_search_enabled')
	);
};
